import React from 'react'
import { Dialog, DialogActions, DialogContent, IconButton } from '@mui/material'
import { X } from 'lucide-react'
import Button from '../../ui/Button'
import { getInfoBadgeClassName } from '../../ui/InfoBadge'
import type { ResolvedAppendixRef } from '../../../utils/appendixRefs'

type ReferencedAppendicesModalProps = {
  open: boolean
  subtitle: string
  refs: ReadonlyArray<ResolvedAppendixRef>
  // Whether references set on this entity may be removed here.
  canRemove: boolean
  onClose: () => void
  onRemove: (key: string) => void
}

function capitalize(text: string): string {
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : text
}

/** Every appendix that applies to a code item, value or group, with where each one comes from. */
export const ReferencedAppendicesModal: React.FC<ReferencedAppendicesModalProps> = ({
  open,
  subtitle,
  refs,
  canRemove,
  onClose,
  onRemove,
}) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth="sm"
    fullWidth
    aria-labelledby="referenced-appendices-title"
    PaperProps={{ sx: { borderRadius: '8px' } }}
  >
    <div className="flex items-start justify-between border-b border-[#E2E8F0] bg-[#F3F9FF] px-5 py-4">
      <div>
        <h2 id="referenced-appendices-title" className="text-lg font-semibold text-gray-900">Referenced appendices</h2>
        <p className="text-xs text-[#1D5176]">{subtitle}</p>
      </div>
      <IconButton size="small" onClick={onClose} aria-label="Close">
        <X className="h-5 w-5 text-[#1D5176]" aria-hidden="true" />
      </IconButton>
    </div>

    <DialogContent sx={{ padding: '16px 20px 8px' }}>
      {refs.length === 0 ? (
        <p className="py-4 text-center text-sm text-gray-500">No appendix is referenced here.</p>
      ) : (
        <ul className="divide-y divide-[#E2E8F0]">
          {refs.map(ref => (
            <li key={`${ref.source}:${ref.key}`} className="flex items-center gap-3 py-2">
              <span className={getInfoBadgeClassName('info', `rounded-full px-2 ${ref.source === 'own' ? 'font-semibold' : 'bg-white'}`)}>
                {ref.letter}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-gray-900">{ref.title}</span>
                <span className="block text-xs text-gray-500">{capitalize(ref.sourceLabel)}</span>
              </span>
              {ref.source === 'own' && canRemove ? (
                <Button
                  type="button"
                  variant="muted"
                  size="sm"
                  aria-label={`Remove appendix ${ref.letter}`}
                  onClick={() => onRemove(ref.key)}
                >
                  Remove
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </DialogContent>

    <DialogActions sx={{ padding: '12px 20px 16px', borderTop: '1px solid #E2E8F0' }}>
      <Button type="button" variant="secondary" size="sm" className="bg-[#EEF6FF]" onClick={onClose}>
        Close
      </Button>
    </DialogActions>
  </Dialog>
)

export default ReferencedAppendicesModal
